import React, { useState, useMemo } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button } from '@heroui/react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GetRowIdParams, themeBalham } from 'ag-grid-enterprise';
import { TDetailedPolygons, TFeature } from '../../../../main-map/mapTypes';


interface PolygonSelectorModalProps {
	isOpen: boolean;
	onClose: () => void;
	onConfirm: (selectedIds: string[]) => void;
	detailedPolygons: TDetailedPolygons | null;
	selectedPolygonIds: string[];
}

interface PolygonRowData {
	id: string;
	region: string;
	country: string;
	portName: string;
}

export const PolygonSelectorModal: React.FC<PolygonSelectorModalProps> = ({
	isOpen,
	onClose,
	onConfirm,
	detailedPolygons,
	selectedPolygonIds,
}) => {
	const [selectedRows, setSelectedRows] = useState<string[]>(selectedPolygonIds);

	// Transform polygon data for the grid
	const rowData = useMemo<PolygonRowData[]>(() => {
		if (!detailedPolygons?.features) return [];

		const mappedData = detailedPolygons.features
			.filter((feature: TFeature) => feature.properties)
			.map((feature: TFeature) => ({
				id: feature.properties!.id,
				region: feature.properties!.region || '',
				country: feature.properties!.country || '',
				portName: feature.properties!.portName || '',
			}));

		// Sort by selection status first (selected rows on top), then by ID
		return mappedData.sort((a, b) => {
			const aSelected = selectedRows.includes(a.id);
			const bSelected = selectedRows.includes(b.id);
			
			// If selection status is different, prioritize selected rows
			if (aSelected && !bSelected) return -1;
			if (!aSelected && bSelected) return 1;
			
			// If both have same selection status, sort by ID
			return String(a.id).localeCompare(String(b.id));
		});
	}, [detailedPolygons, selectedRows]);

	// Column definitions for the grid
	const columnDefs = useMemo<ColDef<PolygonRowData>[]>(
		() => [
			{
				headerName: '',
				headerCheckboxSelection: true,
				checkboxSelection: true,
				width: 50,
				pinned: 'left',
				cellRendererParams: {
					'aria-label': 'Select polygon'
				},
				headerComponentParams: {
					'aria-label': 'Select all polygons'
				}
			},
			{
				headerName: 'ID',
				field: 'id',
				sortable: true,
				filter: 'agTextColumnFilter',
				flex: 1,
				minWidth: 100,
				sort: 'asc', // Default sort ascending
			},
			{
				headerName: 'Region',
				field: 'region',
				sortable: true,
				filter: 'agTextColumnFilter',
				flex: 1,
				minWidth: 150,
			},
			{
				headerName: 'Country',
				field: 'country',
				sortable: true,
				filter: 'agTextColumnFilter',
				flex: 1,
				minWidth: 150,
			},
			{
				headerName: 'Port Name',
				field: 'portName',
				sortable: true,
				filter: 'agTextColumnFilter',
				flex: 1,
				minWidth: 200,
			},
		],
		[],
	);

	const handleSelectionChanged = (event: any) => {
		const selectedNodes = event.api.getSelectedNodes();
		const selectedIds = selectedNodes.map((node: any) => node.data.id);
		setSelectedRows(selectedIds);
	};

	const handleConfirm = () => {
		onConfirm(selectedRows);
		onClose();
	};

	const handleCancel = () => {
		setSelectedRows(selectedPolygonIds); // Reset to original selection
		onClose();
	};

	const getRowId = (params: GetRowIdParams<PolygonRowData>) => {
		return String(params.data.id);
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleCancel}
			size="5xl"
			scrollBehavior="inside"
			classNames={{
				base: 'max-h-[90vh]',
				body: 'p-0',
			}}
		>
			<ModalContent>
				<ModalHeader className="flex flex-col gap-1">
					<h2 className="text-lg font-semibold">Select Polygons</h2>
					<p className="text-sm text-gray-600">
						Choose one or more polygons from the list below. Use filters to narrow down your search.
					</p>
				</ModalHeader>
				<ModalBody className="p-4">
					<div className="ag-theme-alpine h-96 w-full">
						<AgGridReact<PolygonRowData>
							rowData={rowData}
							columnDefs={columnDefs}
							rowSelection={{
								mode: 'multiRow',
								enableClickSelection: false,
								selectAll: 'filtered'
							}}
							onSelectionChanged={handleSelectionChanged}
							getRowId={getRowId}
                            theme={themeBalham}
							defaultColDef={{
								resizable: true,
								sortable: true,
								filter: true,
								floatingFilter: true,
							}}
							suppressMenuHide={true}
							//rowHeight={40}
							//headerHeight={50}
							//floatingFiltersHeight={35}
							onGridReady={(params) => {
								// Select previously selected rows
								if (selectedPolygonIds.length > 0) {
									params.api.forEachNode((node) => {
										if (node.data?.id && selectedPolygonIds.includes(node.data.id)) {
											node.setSelected(true);
										}
									});
								}
							}}
						/>
					</div>
					{selectedRows.length > 0 && (
						<div className="mt-4 p-3 bg-blue-50 rounded-lg">
							<p className="text-sm font-medium text-blue-800">
								Selected: {selectedRows.length} polygon{selectedRows.length !== 1 ? 's' : ''}
							</p>
							<div className="text-xs text-blue-600 mt-1 max-h-20 overflow-y-auto">{selectedRows.join(', ')}</div>
						</div>
					)}
				</ModalBody>
				<ModalFooter>
					<Button className='rounded' size='sm' color="danger" variant="light" onPress={handleCancel}>
						Cancel
					</Button>
					<Button className='rounded' size='sm' color="primary" onPress={handleConfirm} isDisabled={selectedRows.length === 0}>
						Confirm Selection ({selectedRows.length})
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};
